import {
  CHALLENGE_TIMEOUT_MS,
  ChallengeStatus,
  getDocumentChallengeState
} from '@spa/lib/fastly-challenge/challengeState';
import { DispatchGuard, requestSignal, serverActionUrl } from './transport';
import { RecoveryError, RecoveryOutcome } from './errors';
import logger from '@spa/lib/logger';

type GateFailureReason = 'challenge-error' | 'integration-failure' | 'operation-timeout';

const gateOutcomes: Record<GateFailureReason, RecoveryOutcome> = {
  'challenge-error': 'challenge-failed',
  'integration-failure': 'integration-failed',
  'operation-timeout': 'timed-out'
};

/** Resolves once the document challenge is usable; failures and aborts settle unsent. */
export const waitForChallenge = (signal?: AbortSignal, guard?: DispatchGuard): Promise<void> =>
  new Promise((resolve, reject) => {
    const state = getDocumentChallengeState();
    const deadline = Math.min(Date.now() + CHALLENGE_TIMEOUT_MS, guard?.deadline ?? Infinity);
    let waiting = true;
    let evaluating = false;

    const settle = (error: Error | null, publishFailure: boolean): void => {
      if (waiting) {
        waiting = false;
        clearTimeout(timer);
        unsubscribeState();
        unsubscribeGuard?.();
        signal?.removeEventListener('abort', onAbort);
        if (error === null) {
          resolve();
        } else {
          if (publishFailure) state.publishFailure();
          reject(error);
        }
      }
    };
    const fail = (reason: GateFailureReason): void => {
      if (waiting) {
        logger.warn({ reason }, 'Fastly challenge gate failed a Server Action unsent');
        settle(new RecoveryError(gateOutcomes[reason]), true);
      }
    };
    const onAbort = (): void => {
      let error: Error = new DOMException('The operation was aborted.', 'AbortError');
      try {
        guard?.assertActive();
      } catch (failure) {
        error = failure instanceof Error ? failure : new Error(String(failure));
      }
      settle(error, false);
    };
    const evaluate = (): void => {
      if (waiting && !evaluating) {
        evaluating = true;
        try {
          guard?.assertActive();
        } catch (error) {
          settle(error instanceof Error ? error : new Error(String(error)), false);
        }
        if (waiting && signal?.aborted) {
          onAbort();
        } else if (waiting) {
          // Enforcement publishes synchronously and can re-enter this listener.
          state.enforceDownloadDeadline();
          if (waiting) {
            const snapshot = state.getSnapshot();
            if (snapshot.integrationFailure !== null) {
              fail('integration-failure');
            } else if (Date.now() >= deadline) {
              fail('operation-timeout');
            } else if (snapshot.status === ChallengeStatus.ERROR) {
              fail('challenge-error');
            } else if (
              snapshot.enabled === false ||
              (snapshot.enabled === true && snapshot.status === ChallengeStatus.COMPLETE)
            ) {
              settle(null, false);
            }
          }
        }
        evaluating = false;
      }
    };

    const timer = setTimeout(
      () => {
        // Elapsed timer time stays terminal even if the wall clock moved backwards.
        evaluate();
        if (waiting) fail('operation-timeout');
      },
      Math.max(0, deadline - Date.now())
    );
    signal?.addEventListener('abort', onAbort);
    const unsubscribeState = state.subscribe(evaluate);
    const unsubscribeGuard = guard?.subscribe(evaluate);
    evaluate();
  });

export function createChallengeGateFetch(
  original: typeof fetch,
  origin: string,
  documentUrl: () => string = (): string => origin
): typeof fetch {
  return async (input, init): Promise<Response> => {
    if (serverActionUrl(input, init, origin, documentUrl()) !== null) {
      await waitForChallenge(requestSignal(input, init));
    }
    return original(input, init);
  };
}
